import { AppContext, Features } from "@lightspeed-invoicing/core-client/frontend";
import React from 'react';
import { FormattedMessage } from 'react-intl';
import EventBus from "../../../common/EventBus.js";
import Icon from "../../../common/icon/icon.component.jsx";

class SectionContentTypes {

    static getInputComponent(contentType, value, variables, onChange) {
        try {
            const Input = require("./" + contentType + "/input.component")
            return <Input.default value={value} onChange={onChange} variables={variables} />
        } catch (e) {
            return ''
        }
    }

    static openSectionEditor(sectionId) {
        EventBus.dispatch(EventBus.TopicProposalSectionEditorOpen, {
            sectionId: sectionId
        })
    }

    static addNewSection(contentType, position) {
        EventBus.dispatch(EventBus.TopicProposalSectionEditorOpen, {
            contentType: contentType,
            position: position,
        })
    }

    static getUniquesColorsFromVariables(variables) {
        return [
            '#ffffff',
            '#000000',
            variables.global.primaryColor,
            variables.global.secondaryColor,
            variables.global.backgroundColor,
            variables.global.groupTitleBackgroundColor,
            variables.global.bannerColor,
            variables.global.buttonColor,
            variables.global.additionalColor1,
            variables.global.additionalColor2,
            variables.global.additionalColor3,
        ].filter((value, index, self) => self.indexOf(value) === index)
    }

    static getSectionComponent(section, variables, isPdf, isDummy, isSample, isWord, editable, key, onEdit, mobile, templateEmail) {
        try {
            let component = 'section'
            if (isWord) {
                component = 'word'
            }

            let Section
            try {
                Section = require("./" + section.attributes.contentType + "/" + component + ".component.jsx")
            } catch {
                Section = require("./" + section.attributes.contentType + "/" + component + ".component.tsx")
            }

            if (!Section) {
                return ''
            }
            return <Section.default
                id={'section-' + section.id}
                section={section}
                variables={variables}
                isPdf={isPdf}
                isDummy={isDummy}
                isSample={isSample}
                key={key}
                editable={editable}
                onEdit={onEdit}
                mobile={mobile}
                templateEmail={templateEmail}
            />
        } catch (e) {
            return ''
        }
    }

    static getById(contentType, documentType) {
        const res = this.getAll('all', documentType).filter(c => c.id === contentType)
        if (res.length > 0) {
            return res[0]
        } else {
            return {}
        }
    }

    static getInternalName(section) {
        switch (section.attributes.contentType) {
            case 'title':
                return section.attributes.content.title
            case 'document-info':
            case 'empty-space':
            case 'approve-section':
            case 'project-images':
            case 'project-files':
            case 'project-description':
            case 'pricing-table':
            case 'project-title':
            case 'line-separator':
                return this.getById(section.attributes.contentType).name
            default:
                return section.attributes.internalName
        }
    }

    static getIcon(contentType) {
        return this.getById(contentType).icon
    }

    static getAll(type, documentType) {
        const types = [
            {
                id: 'title',
                icon: <Icon value={"heading"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.title.name"
                    defaultMessage="Title"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.title.description"
                    defaultMessage="Title is a bolder and bigger text that you can put on top of paragraphs"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'text',
                icon: <Icon value={"align-left"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.text.name"
                    defaultMessage="Text"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.text.description"
                    defaultMessage="The perfect section to describe your company, terms and conditions or simply how well you deliver projects"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'document-info',
                icon: <Icon value={"address-card"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.document_info.name"
                    defaultMessage="Contact information"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.document_info.description"
                    defaultMessage="This section contains the customer information, your company information and the quote reference with the pdf download button"
                />,
                mandatory: true,
                notAllowedIn: ['price-list'],
            },
            {
                id: 'checkout',
                icon: <Icon value={"shopping-cart"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.checkout.name"
                    defaultMessage="Checkout section"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.checkout.description"
                    defaultMessage="This section contains a form to allow customer to enter their information and pay for their selection."
                />,
                mandatory: true,
                notAllowedIn: ['quote', 'invoice', 'packing-slip', 'account-statement', 'scheduling-page'],
            },
            {
                id: 'text-and-image',
                icon: <Icon value={"columns"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.text_and_image.name"
                    defaultMessage="Text and image"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.text_and_image.description"
                    defaultMessage="This section has tow columns, one with an image and one with the text"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'text-and-image-row',
                icon: <Icon value={"columns"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.text-and-image-row.name"
                    defaultMessage="Text and Image row"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.text-and-image-row.description"
                    defaultMessage="In this section you can add a row composed of several images and texts"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'image',
                icon: <Icon value={"image"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.image.name"
                    defaultMessage="Image"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.image.description"
                    defaultMessage="In this section you can add a large image to illustrate your quote"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'image-row',
                icon: <Icon value={"columns"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.imagerow.name"
                    defaultMessage="Image row"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.imagerow.description"
                    defaultMessage="In this section you can add a row composed of several images"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'video',
                icon: <Icon value={['fab', 'youtube']} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.video.name"
                    defaultMessage="Video"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.video.description"
                    defaultMessage="In this section you can add a video to illustrate your quote"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'image-gallery',
                icon: <Icon value={"images"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.image_gallery.name"
                    defaultMessage="Image gallery"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.image_gallery.description"
                    defaultMessage="This section can contains a list of images that will be viewable via a gallery"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'file-gallery',
                icon: <Icon value={"file-pdf"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.file_gallery.name"
                    defaultMessage="File gallery"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.file_gallery.description"
                    defaultMessage="This section can contains a list of files that will be downloadable by your customers"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'line-separator',
                icon: <Icon value={"minus"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.line_separator.name"
                    defaultMessage="Line separator"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.line_separator.description"
                    defaultMessage="This section is a simple gray line to separate content"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'empty-space',
                icon: <Icon value={"expand"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.empty_space.name"
                    defaultMessage="Separator"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.empty_space.description"
                    defaultMessage="This is a separator between two sections"
                />,
                mandatory: false,
                notAllowedIn: [],
            },
            {
                id: 'approve-section',
                icon: <Icon value={"check"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.approve_section.name"
                    defaultMessage="Approve section"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.approve_section.description"
                    defaultMessage="This section contains the button to approve the quote"
                />,
                mandatory: true,
                notAllowedIn: ['price-list', 'order', 'invoice', 'packing-slip', 'account-statement', 'scheduling-page'],
            },
            {
                id: 'project-title',
                icon: <Icon value={"heading"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.project_title.name"
                    defaultMessage="Project title"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.project_title.description"
                    defaultMessage="Title of the project be that can be added during the quote creation"
                />,
                mandatory: false,
                disabled: true,
                notAllowedIn: [],
            },
            {
                id: 'project-description',
                icon: <Icon value={"align-left"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.project_description.name"
                    defaultMessage="Project description"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.project_description.description"
                    defaultMessage="This section contains project description that can be edited during the quote creation"
                />,
                mandatory: false,
                disabled: true,
                notAllowedIn: [],
            },
            {
                id: 'project-images',
                icon: <Icon value={"images"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.project_images.name"
                    defaultMessage="Project images"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.project_images.description"
                    defaultMessage="This section contains project images as a gallery that can be added during the quote creation"
                />,
                mandatory: false,
                disabled: true,
                notAllowedIn: [],
            },
            {
                id: 'project-files',
                icon: <Icon value={"file-pdf"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.project_files.name"
                    defaultMessage="Project files"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.project_files.description"
                    defaultMessage="This section contains project files as a gallery that can be added during the quote creation"
                />,
                mandatory: false,
                disabled: true,
                notAllowedIn: [],
            },
            {
                id: 'pricing-table',
                icon: <Icon value={"dollar-sign"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.pricing_table.name"
                    defaultMessage="Pricing table"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.pricing_table.description"
                    defaultMessage="This section contains the items and totals of your quote"
                />,
                mandatory: false,
                notAllowedIn: ['scheduling-page', 'account-statement'],
            },
            {
                id: 'customer-account-statement',
                icon: <Icon value={"file-invoice"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.customer_account_statement.name"
                    defaultMessage="Customer account statement"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.customer_account_statement.description"
                    defaultMessage="This section contains a table of owned invoices and credit memos available"
                />,
                mandatory: false,
                notAllowedIn: ['price-list', 'scheduling-page'],
            },
            {
                id: 'signature',
                icon: <Icon value={"signature"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.signature.name"
                    defaultMessage="Signature"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.signature.description"
                    defaultMessage="This section contains a signature pad to allow your customer to sign electronically"
                />,
                mandatory: false,
                notAllowedIn: ['price-list', 'scheduling-page'],
                premiumOnly: true,
            },
            {
                id: 'customer-ageing-report',
                icon: <Icon value={"history"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.customer_ageing_report.name"
                    defaultMessage="Customer ageing report"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.customer_ageing_report.description"
                    defaultMessage="This section contains the ageing report of your customer"
                />,
                mandatory: false,
                notAllowedIn: ['price-list', 'scheduling-page'],
            },
            {
                id: 'custom-form',
                icon: <Icon value={"table"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.custom_form.name"
                    defaultMessage="Custom form"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.custom_form.description"
                    defaultMessage="This section contains a custom field"
                />,
                mandatory: false,
                notAllowedIn: ['account-statement', 'scheduling-page', 'credit-memo'],
            },
            {
                id: 'order-status',
                icon: <Icon value={"tasks"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.order_status.name"
                    defaultMessage="Order status"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.order_status.description"
                    defaultMessage="This section contains the status of the order"
                />,
                mandatory: false,
                notAllowedIn: ['quote', 'invoice', 'price-list', 'packing-slip', 'account-statement', 'scheduling-page', 'credit-memo'],
            },
            {
                id: 'scheduler',
                icon: <Icon value={"calendar-day"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.scheduler.name"
                    defaultMessage="Scheduler"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.scheduler.description"
                    defaultMessage="This section contains a scheduler to book an appointment"
                />,
                mandatory: true,
                notAllowedIn: [],
            },
        ]

        const showPaymentContent = AppContext.hasFeatureIntegration(Features.IntegratedPayment) || AppContext.hasFeatureIntegration(Features.FeatureExternalCheckoutPayment)
        if (showPaymentContent) {
            types.push({
                id: 'payment',
                icon: <Icon value={"credit-card"} />,
                name: <FormattedMessage
                    id="templates.sections.content_types.payment.name"
                    defaultMessage="Payment section"
                />,
                description: <FormattedMessage
                    id="templates.sections.content_types.payment.description"
                    defaultMessage="This section contains a payment form to pay the invoice online"
                />,
                mandatory: false,
                notAllowedIn: ['quote', 'price-list', 'packing-slip', 'scheduling-page', 'credit-memo'],
                premiumOnly: true,
            })
        }

        return types.filter(section => !section.notAllowedIn.includes(type))
    }
}

export default SectionContentTypes


// WEBPACK FOOTER //
// ./src/template/sections/content/ContentTypes.jsx